import React, { useEffect, useState } from "react";
import axios from "axios";
import { backend_url } from "../App";
import "./CSS/Settings.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAdminId } from "../utils/roleUtils";

const initForm = {
    storeName: "",
    supportEmail: "",
    supportPhone: "",
    address: "",
    gstNumber: "",
    taxRate: 5,
    shippingCost: 0,
    freeShippingThreshold: 0,
    maintenanceMode: false,
    announcementText: "",
    announcementEnabled: false,
};

const Settings = () => {
    const [form, setForm] = useState(initForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => { fetchSettings(); }, []);

    const fetchSettings = async () => {
        try {
            const res = await axios.get(`${backend_url}/api/settings`);
            if (res.data.success) setForm({ ...initForm, ...res.data.settings });
        } catch {
            toast.error("Failed to load settings");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await axios.put(`${backend_url}/api/settings`, {
                ...form,
                adminId: getAdminId(),
                adminName: sessionStorage.getItem("adminName"),
                adminRole: sessionStorage.getItem("adminRole"),
            });
            if (res.data.success) {
                toast.success("Settings saved successfully");
                setForm({ ...initForm, ...res.data.settings });
            } else {
                toast.error("Failed to save settings");
            }
        } catch {
            toast.error("Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="settings-page">
            <div className="settings-banner">
                <h1 className="settings-title">Store Settings</h1>
                <p className="settings-subtitle">Manage store info, tax and shipping configuration</p>
            </div>

            <div className="settings-content">
                {loading ? (
                    <div className="settings-loading">Loading settings...</div>
                ) : (
                    <form className="settings-form" onSubmit={handleSave}>

                        <div className="settings-card">
                            <h3>🏬 Store Information</h3>
                            <div className="settings-grid">
                                <div className="settings-field">
                                    <label>Store Name</label>
                                    <input
                                        value={form.storeName}
                                        onChange={(e) => handleChange("storeName", e.target.value)}
                                        placeholder="Aavryaa FabHub"
                                    />
                                </div>
                                <div className="settings-field">
                                    <label>Support Email</label>
                                    <input
                                        type="email"
                                        value={form.supportEmail}
                                        onChange={(e) => handleChange("supportEmail", e.target.value)}
                                        placeholder="support@aavryaa.com"
                                    />
                                </div>
                                <div className="settings-field">
                                    <label>Support Phone</label>
                                    <input
                                        value={form.supportPhone}
                                        onChange={(e) => handleChange("supportPhone", e.target.value)}
                                        placeholder="+91 98765 43210"
                                    />
                                </div>
                                <div className="settings-field">
                                    <label>GSTIN</label>
                                    <input
                                        value={form.gstNumber}
                                        onChange={(e) => handleChange("gstNumber", e.target.value)}
                                        placeholder="33AAAAA0000A1Z5"
                                    />
                                </div>
                                <div className="settings-field settings-field-wide">
                                    <label>Store Address</label>
                                    <textarea
                                        value={form.address}
                                        onChange={(e) => handleChange("address", e.target.value)}
                                        placeholder="Store pickup / registered address"
                                        rows={3}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="settings-card">
                            <h3>💰 Tax & Shipping</h3>
                            <div className="settings-grid">
                                <div className="settings-field">
                                    <label>Tax / GST Rate (%)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        value={form.taxRate}
                                        onChange={(e) => handleChange("taxRate", e.target.value)}
                                    />
                                </div>
                                <div className="settings-field">
                                    <label>Shipping Cost (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={form.shippingCost}
                                        onChange={(e) => handleChange("shippingCost", e.target.value)}
                                    />
                                </div>
                                <div className="settings-field">
                                    <label>Free Shipping Above (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={form.freeShippingThreshold}
                                        onChange={(e) => handleChange("freeShippingThreshold", e.target.value)}
                                        placeholder="0 = disabled"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="settings-card">
                            <h3>📢 Announcement Bar</h3>
                            <label className="settings-toggle-row">
                                <span>
                                    <strong>Show announcement on store</strong>
                                    <span className="settings-toggle-hint">Scrolling bar at the top of the customer website</span>
                                </span>
                                <input
                                    type="checkbox"
                                    checked={form.announcementEnabled}
                                    onChange={(e) => handleChange("announcementEnabled", e.target.checked)}
                                />
                            </label>
                            <div className="settings-field settings-field-wide" style={{ marginTop: 12 }}>
                                <label>Announcement Text</label>
                                <textarea
                                    value={form.announcementText}
                                    onChange={(e) => handleChange("announcementText", e.target.value)}
                                    placeholder="e.g. ✨ Festive Sale! Flat 15% OFF on all sarees till Sunday — use code FESTIVE15 🛍️"
                                    rows={2}
                                    maxLength={300}
                                />
                                <span className="settings-toggle-hint">
                                    {form.announcementText?.length || 0}/300 · Emojis are supported
                                </span>
                            </div>
                        </div>

                        <div className="settings-card">
                            <h3>⚙️ Store Status</h3>
                            <label className="settings-toggle-row">
                                <span>
                                    <strong>Maintenance Mode</strong>
                                    <span className="settings-toggle-hint">Temporarily disable checkout / show maintenance banner</span>
                                </span>
                                <input
                                    type="checkbox"
                                    checked={form.maintenanceMode}
                                    onChange={(e) => handleChange("maintenanceMode", e.target.checked)}
                                />
                            </label>
                        </div>

                        <div className="settings-footer">
                            <button type="submit" className="settings-save-btn" disabled={saving}>
                                {saving ? "Saving..." : "Save Settings"}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            <ToastContainer />
        </div>
    );
};

export default Settings;
